import React, { useMemo } from 'react';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { iterationsForMonth, summarizeIteration } from './iterationsBootstrap.js';
import { formatShortDate } from './format.js';

export const chipSummaryFromRun = (summary) => {
  if (!summary) return null;
  return {
    openCount: Number(summary.openCount ?? summary.toReview ?? 0),
    reviewedCount: Number(summary.reviewedCount ?? summary.reviewed ?? 0),
    remainingAbs: Number(summary.remainingAbs ?? summary.remainingGmCpqAbs ?? 0),
  };
};

const optionLabel = (iteration, summary) => {
  const date = formatShortDate(iteration.createdAt);
  const mode = iteration.importMode || iteration.import_mode;
  const processingStatus = iteration.processingStatus || iteration.processing_status;
  const prefix = mode === 'historical_backfill' ? 'Historical · ' : '';
  if (processingStatus === 'processing') return `${prefix}Round ${iteration.number} · Processing`;
  if (processingStatus === 'failed') return `${prefix}Round ${iteration.number} · Failed`;
  if (!summary) return `${prefix}Round ${iteration.number} · ${date}`;
  return `${prefix}Round ${iteration.number} · ${date} · ${summary.openCount.toLocaleString()} open`;
};

const IterationRoundsBar = ({
  monthId,
  iterations,
  activeIterationId,
  onSelect,
  onCreate,
  onRequestDelete,
  createDisabled = false,
  gmRows,
  cpqRows,
  dpeDealers,
}) => {
  const forMonth = useMemo(() => iterationsForMonth(iterations, monthId), [iterations, monthId]);
  const summaries = useMemo(() => {
    const map = {};
    for (const it of forMonth) {
      map[it.id] = chipSummaryFromRun(it.summary) || summarizeIteration({ iteration: it, gmRows, cpqRows, dpeDealers });
    }
    return map;
  }, [forMonth, gmRows, cpqRows, dpeDealers]);
  const activeIteration = forMonth.find((iteration) => iteration.id === activeIterationId) || null;
  const activeProcessingStatus = activeIteration?.processingStatus || activeIteration?.processing_status || null;
  const deleteDisabled = !activeIteration || activeProcessingStatus === 'processing';
  const createTooltip = createDisabled
    ? 'A billing round is processing. Wait for it to finish before creating another round.'
    : 'Create a new reconciliation round';
  const deleteTooltip = activeProcessingStatus === 'processing'
    ? 'Round upload is still processing'
    : activeIteration ? `Delete Round ${activeIteration.number}` : 'No round selected';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, fontSize: 11 }}
          >
            Reconciliation rounds
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 }, flex: { xs: 1, sm: '0 1 320px' } }}>
            <InputLabel id="billing-round-select-label">Round</InputLabel>
            <Select
              labelId="billing-round-select-label"
              value={activeIterationId || ''}
              label="Round"
              onChange={(event) => onSelect(event.target.value)}
              displayEmpty
            >
              {forMonth.length ? forMonth.map((iteration) => (
                <MenuItem key={iteration.id} value={iteration.id}>
                  {optionLabel(iteration, summaries[iteration.id])}
                </MenuItem>
              )) : (
                <MenuItem value="" disabled>No rounds yet</MenuItem>
              )}
            </Select>
          </FormControl>
          <Tooltip title={deleteTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={() => activeIteration && onRequestDelete({ iteration: activeIteration, summary: summaries[activeIteration.id] })}
                disabled={deleteDisabled}
                aria-label={activeIteration ? `Delete Round ${activeIteration.number}` : 'Delete selected round'}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={createTooltip}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onCreate}
                disabled={createDisabled}
                sx={{
                  textTransform: 'none',
                  borderStyle: 'dashed',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderStyle: 'solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {createDisabled ? 'Processing...' : 'New round'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default IterationRoundsBar;
